import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import {
  Database,
  Server,
  Activity,
  AlertCircle,
  CheckCircle2,
  RefreshCw,
  Trash2,
  User,
  Clock,
  Code,
} from 'lucide-react';

/**
 * P1: Admin Diagnostics Panel
 * Shows system health and diagnostic information for troubleshooting
 */
export default function AdminDiagnosticsPage() {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [diagnostics, setDiagnostics] = useState(null);
  const [serviceWorkerInfo, setServiceWorkerInfo] = useState(null);

  const fetchDiagnostics = async () => {
    try {
      setLoading(true);
      const response = await api.get('/admin/diagnostics');
      setDiagnostics(response.data);
    } catch (error) {
      console.error('Failed to fetch diagnostics:', error);
      toast.error('Failed to load diagnostics');
    } finally {
      setLoading(false);
    }
  };

  const clearErrorLog = async () => {
    try {
      await api.post('/admin/diagnostics/clear-errors');
      toast.success('Error log cleared');
      fetchDiagnostics();
    } catch (error) {
      toast.error('Failed to clear error log');
    }
  };

  const runDbCheck = async () => {
    try {
      const response = await api.get('/admin/diagnostics/db-check');
      const results = response.data;
      
      const allPassed = results.overall_status === 'healthy';
      if (allPassed) {
        toast.success('All database checks passed');
      } else {
        toast.warning('Some database checks failed - see details');
      }
      
      fetchDiagnostics();
    } catch (error) {
      toast.error('Database check failed');
    }
  };

  // Get service worker info
  useEffect(() => {
    const getSwInfo = async () => {
      if ('serviceWorker' in navigator) {
        try {
          const registrations = await navigator.serviceWorker.getRegistrations();
          setServiceWorkerInfo({
            registered: registrations.length > 0,
            count: registrations.length,
            active: registrations.some(r => r.active),
          });
        } catch (e) {
          setServiceWorkerInfo({ error: e.message });
        }
      } else {
        setServiceWorkerInfo({ supported: false });
      }
    };
    getSwInfo();
  }, []);

  useEffect(() => {
    fetchDiagnostics();
  }, []);

  const StatusBadge = ({ status }) => {
    const isHealthy = status === 'healthy' || status === 'pass' || status === true || status === 'running';
    return (
      <Badge 
        className={isHealthy 
          ? 'bg-emerald-500/20 text-emerald-400 border-emerald-500/50' 
          : 'bg-red-500/20 text-red-400 border-red-500/50'
        }
      >
        {isHealthy ? (
          <CheckCircle2 className="w-3 h-3 mr-1" />
        ) : (
          <AlertCircle className="w-3 h-3 mr-1" />
        )}
        {String(status)}
      </Badge>
    );
  };

  if (loading && !diagnostics) {
    return (
      <div className="min-h-screen bg-[#09090b] p-6 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-amber-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#09090b] p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Activity className="w-6 h-6 text-amber-400" />
              System Diagnostics
            </h1>
            <p className="text-zinc-400 text-sm mt-1">
              Monitor system health and troubleshoot issues
            </p>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={runDbCheck}
              className="bg-blue-500 hover:bg-blue-600"
            >
              <Database className="w-4 h-4 mr-2" />
              Run DB Check
            </Button>
            <Button
              onClick={fetchDiagnostics}
              className="bg-amber-500 hover:bg-amber-600"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Quick Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {/* Database Status */}
          <Card className="bg-[#18181b] border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Database className="w-5 h-5 text-amber-400" />
                  <span className="text-zinc-300">Database</span>
                </div>
                <StatusBadge status={diagnostics?.database?.connected ? 'healthy' : 'error'} />
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                {diagnostics?.database?.name || 'N/A'}
              </p>
            </CardContent>
          </Card>

          {/* API Status */}
          <Card className="bg-[#18181b] border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Server className="w-5 h-5 text-amber-400" />
                  <span className="text-zinc-300">API</span>
                </div>
                <StatusBadge status={diagnostics?.api?.status} />
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                Python {diagnostics?.api?.python_version || 'N/A'}
              </p>
            </CardContent>
          </Card>

          {/* Service Worker */}
          <Card className="bg-[#18181b] border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Code className="w-5 h-5 text-amber-400" />
                  <span className="text-zinc-300">Service Worker</span>
                </div>
                <StatusBadge status={serviceWorkerInfo?.active ? 'active' : 'inactive'} />
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                Version: {diagnostics?.service_worker_version || 'N/A'}
              </p>
            </CardContent>
          </Card>

          {/* Current User */}
          <Card className="bg-[#18181b] border-zinc-800">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5 text-amber-400" />
                  <span className="text-zinc-300">Logged In</span>
                </div>
                <Badge className="bg-blue-500/20 text-blue-400 border-blue-500/50">
                  {diagnostics?.current_user?.role || 'N/A'}
                </Badge>
              </div>
              <p className="text-xs text-zinc-500 mt-2 truncate">
                {diagnostics?.current_user?.email || 'N/A'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Detailed Info */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Database Details */}
          <Card className="bg-[#18181b] border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Database className="w-5 h-5 text-amber-400" />
                Database Details
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-zinc-500">Database Name</span>
                  <p className="text-white font-mono">{diagnostics?.database?.name}</p>
                </div>
                <div>
                  <span className="text-zinc-500">Connected At</span>
                  <p className="text-white">
                    {diagnostics?.database?.connected_at 
                      ? new Date(diagnostics.database.connected_at).toLocaleString()
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-zinc-500">Last Health Check</span>
                  <p className="text-white">
                    {diagnostics?.database?.last_health_check
                      ? new Date(diagnostics.database.last_health_check).toLocaleString()
                      : 'N/A'}
                  </p>
                </div>
                <div>
                  <span className="text-zinc-500">Health Status</span>
                  <p className="text-white">{diagnostics?.database?.last_health_status || 'N/A'}</p>
                </div>
              </div>
              
              {/* Collection Counts */}
              {diagnostics?.database?.collections && (
                <div>
                  <span className="text-zinc-500 text-sm">Collections</span>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {Object.entries(diagnostics.database.collections).slice(0, 9).map(([name, count]) => (
                      <div key={name} className="bg-[#09090b] rounded p-2 text-xs">
                        <span className="text-zinc-400">{name}</span>
                        <p className="text-white font-mono">{count}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Environment Config */}
          <Card className="bg-[#18181b] border-zinc-800">
            <CardHeader className="pb-2">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <Server className="w-5 h-5 text-amber-400" />
                Environment Configuration
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {diagnostics?.environment && Object.entries(diagnostics.environment).map(([key, value]) => (
                <div key={key} className="flex items-center justify-between py-2 border-b border-zinc-800">
                  <span className="text-zinc-400 font-mono text-sm">{key}</span>
                  <StatusBadge status={value ? 'set' : 'missing'} />
                </div>
              ))}
              
              <div className="pt-4">
                <span className="text-zinc-500 text-sm">App Version</span>
                <p className="text-white font-mono">{diagnostics?.app_version || 'N/A'}</p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Recent Errors */}
        <Card className="bg-[#18181b] border-zinc-800">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-lg text-white flex items-center gap-2">
                <AlertCircle className="w-5 h-5 text-red-400" />
                Recent API Errors
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={clearErrorLog}
                className="text-red-400 border-red-400/50 hover:bg-red-400/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Clear Log
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {diagnostics?.recent_errors?.length > 0 ? (
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {diagnostics.recent_errors.map((error, idx) => (
                  <div 
                    key={idx} 
                    className="p-3 bg-[#09090b] rounded border border-red-500/20"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-red-400 font-mono text-sm">{error.endpoint}</span>
                      <span className="text-zinc-500 text-xs">
                        {new Date(error.timestamp).toLocaleString()}
                      </span>
                    </div>
                    <p className="text-zinc-300 text-sm font-mono break-all">{error.error}</p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-zinc-500">
                <CheckCircle2 className="w-12 h-12 mx-auto mb-2 text-emerald-500/50" />
                <p>No recent errors logged</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timestamp */}
        <div className="text-center text-zinc-500 text-sm">
          <Clock className="w-4 h-4 inline mr-1" />
          Last updated: {diagnostics?.timestamp ? new Date(diagnostics.timestamp).toLocaleString() : 'N/A'}
        </div>
      </div>
    </div>
  );
}
